import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VeriskAuthService {
  private readonly VERISK_TOKEN_KEY = 'veriskToken';
  private readonly VERISK_USER_DATA_KEY = 'veriskUserData';

  constructor(private http: HttpClient) {}

  /**
   * Validates Verisk token by calling backend API
   * @param token - The Verisk access token
   * @returns Observable with validation response containing user data
   */
  validateVeriskUser(token: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'IgnoreInterceptor': '' // Prevent AuthInterceptor from adding another token
      })
    };

    return this.http.get<any>(`${environment.WebApiURL}api/verisk/validate`, httpOptions).pipe(
      map((response: any) => {
        // Store token and user data on successful validation
        if (response && response.statusCode === 200 && response.data) {
          this.storeVeriskToken(token);
          this.storeVeriskUserData(response.data);
        }
        return response;
      }),
      catchError((error: any) => {
        // Clear stored data on validation failure
        this.clearVeriskData();
        return throwError(() => error);
      })
    );
  }

  /**
   * Gets the stored Verisk token from localStorage or cookies
   * Note: If Verisk sends token in request headers, the backend should forward it via cookie
   * @returns The Verisk token or null if not found
   */
  getVeriskToken(): string | null {
    // First check localStorage
    const storedToken = localStorage.getItem(this.VERISK_TOKEN_KEY);
    if (storedToken) {
      return storedToken;
    }

    // Then check cookies (in case backend forwarded header token to cookie)
    const cookieToken = this.getCookie('veriskToken') || this.getCookie('verisk_access_token');
    if (cookieToken) {
      // Store in localStorage for future use
      this.storeVeriskToken(cookieToken);
      return cookieToken;
    }

    return null;
  }

  /**
   * Gets a cookie value by name
   * @param name - Cookie name
   * @returns Cookie value or null if not found
   */
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }

  /**
   * Stores the Verisk token in localStorage
   * @param token - The Verisk access token
   */
  storeVeriskToken(token: string): void {
    localStorage.setItem(this.VERISK_TOKEN_KEY, token);
  }

  /**
   * Gets the stored Verisk user data from localStorage
   * @returns The user data object or null if not found
   */
  getVeriskUserData(): any {
    const userData = localStorage.getItem(this.VERISK_USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Stores Verisk user data in localStorage
   * Also merges with existing currentUserData if needed
   * @param userData - The user data object from validation response
   */
  storeVeriskUserData(userData: any): void {
    // Store Verisk-specific user data
    localStorage.setItem(this.VERISK_USER_DATA_KEY, JSON.stringify(userData));

    // Merge with existing currentUserData structure if it exists
    const existingUserData = JSON.parse(localStorage.getItem('currentUserData') || '{}');
    const mergedUserData = {
      ...existingUserData,
      ...userData,
      accessToken: this.getVeriskToken() || existingUserData.accessToken,
      isVeriskUser: true
    };
    localStorage.setItem('currentUserData', JSON.stringify(mergedUserData));
  }

  /**
   * Clears all Verisk-related data from localStorage
   */
  clearVeriskData(): void {
    localStorage.removeItem(this.VERISK_TOKEN_KEY);
    localStorage.removeItem(this.VERISK_USER_DATA_KEY);
    // Best-effort: clear non-HttpOnly cookies that may be used for Verisk token forwarding.
    // HttpOnly cookies must be cleared server-side via api/VeriskSso/logout.
    this.expireCookie('veriskToken');
    this.expireCookie('verisk_access_token');
  }

  private expireCookie(name: string): void {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    } catch {
      // ignore
    }
  }

  /**
   * Checks if a Verisk token exists in localStorage
   * @returns true if token exists, false otherwise
   */
  hasVeriskToken(): boolean {
    return !!this.getVeriskToken();
  }

  /**
   * True when the current session is an explicit Verisk SSO session (not a stale token left after normal login).
   * Use for menu/UI that should apply only to Verisk users.
   */
  isVeriskSsoSession(): boolean {
    if (!this.hasVeriskToken()) {
      return false;
    }
    try {
      const data = JSON.parse(localStorage.getItem('currentUserData') || '{}');
      return data?.isVeriskUser === true;
    } catch {
      return false;
    }
  }
}
